/**
 * ClocksPanel - the clocks: a name, and segments that fill.
 *
 * Everyone reads the ones shown to them. Whoever directs (`canDirect`) also
 * makes them, fills and empties them a segment at a time, and sets how each
 * runs (v9.5.11): who sees it (the switch for the whole table, or "Seen
 * by..." for the players, a vox group or named people), whether each
 * segment is a beat of time passing, whether it counts DOWN, whether it
 * fills a segment every combat round by itself, and which kept board entry
 * it shows when it is full. A kept clock is drawn with a dashed edge.
 *
 * The same clocks are on the mantel under the header in every mode
 * (`mantel/ClockMantel`); this panel is where they are made and set.
 *
 * @param {object} props
 * @param {import('../../hooks/useStoryBoards').Clocks} props.clocks
 * @param {boolean} props.canDirect
 * @param {object[]} [props.people], [props.channels] - who a clock may be shown to
 * @param {{id: string, title: string, text: string, visible: boolean}[]} [props.entries] - the board; a full clock may show a kept one
 */
import { useState } from 'react';
import AudienceDialog from '../AudienceDialog';
import { audienceForReveal } from '../../engine/handouts/reveal';
import common from '../../styles/common.module.css';
import styles from '../../styles/Story.module.css';
import Rocker from '../controls/Rocker';
import { MAX_SEGMENTS, MIN_SEGMENTS, countText } from '../../engine/narrative/clocks';

const SIZES = Array.from({ length: MAX_SEGMENTS - MIN_SEGMENTS + 1 }, (_, i) => MIN_SEGMENTS + i);

function Segments({ clock }) {
  return (
    <div
      className={styles.segments}
      role="meter"
      aria-label={clock.name}
      aria-valuemin={0}
      aria-valuemax={clock.segments}
      aria-valuenow={clock.filled}
      aria-valuetext={`${clock.filled} of ${clock.segments}`}
    >
      {Array.from({ length: clock.segments }, (_, i) => (
        <span key={i} className={styles.segment} data-filled={i < clock.filled || undefined} />
      ))}
    </div>
  );
}

/** How a clock runs: the three switches and what it shows when full. */
function ClockRunning({ clock, clocks, kept }) {
  const set = (patch) => clocks.edit(clock.id, patch);
  return (
    <div className={styles.tools}>
      <Rocker
        label="Each segment is a beat"
        testId="clock-marks-time"
        on={clock.marksTime}
        onToggle={() => set({ marksTime: !clock.marksTime })}
      />
      <Rocker
        label="Counts down"
        testId="clock-countdown"
        on={clock.direction === 'down'}
        onToggle={() => set({ direction: clock.direction === 'down' ? 'up' : 'down' })}
      />
      <Rocker
        label="Fills each round"
        testId="clock-per-round"
        on={clock.perRound}
        onToggle={() => set({ perRound: !clock.perRound })}
      />
      {(kept.length > 0 || clock.riders?.reveals) && (
        <label className={styles.field}>
          When full, show
          <select
            className={styles.input}
            data-testid="clock-reveals"
            value={clock.riders?.reveals ?? ''}
            onChange={(e) => set({ riders: { reveals: e.target.value } })}
          >
            <option value="">nothing</option>
            {kept.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title || e.text.slice(0, 40)}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

/** The director's controls for one clock. */
function ClockTools({ clock, clocks, onAudience }) {
  return (
    <div className={styles.tools}>
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`Empty a segment of ${clock.name}`}
        disabled={clock.filled === 0}
        onClick={() => clocks.advance(clock.id, -1)}
      >
        -
      </button>
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`Fill a segment of ${clock.name}`}
        disabled={clock.filled >= clock.segments}
        onClick={() => clocks.advance(clock.id, 1)}
      >
        +
      </button>
      <Rocker
        label="The table sees it"
        testId="clock-shown"
        on={clock.visible}
        onToggle={() => clocks.edit(clock.id, { visible: !clock.visible })}
      />
      <button type="button" className={common.btnSmall} data-testid="clock-audience" onClick={onAudience}>
        Seen by…
      </button>
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`Remove ${clock.name}`}
        onClick={() => clocks.remove(clock.id)}
      >
        Remove
      </button>
    </div>
  );
}

function ClockItem({ clock, clocks, canDirect, kept, onAudience }) {
  return (
    <li className={styles.item} data-kept={!clock.visible || undefined} data-testid="clock">
      <div className={styles.head}>
        <span className={styles.name}>{clock.name}</span>
        <span className={styles.count}>{countText(clock)}</span>
      </div>
      <Segments clock={clock} />
      {canDirect && <ClockTools clock={clock} clocks={clocks} onAudience={onAudience} />}
      {canDirect && <ClockRunning clock={clock} clocks={clocks} kept={kept} />}
    </li>
  );
}

function AddClock({ onAdd }) {
  const [name, setName] = useState('');
  const [segments, setSegments] = useState(6);
  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd(name, segments);
    setName('');
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <label className={styles.field}>
        New clock
        <input
          className={styles.input}
          data-testid="clock-name"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className={`${styles.field} ${styles.narrow}`}>
        Segments
        <select
          className={styles.input}
          data-testid="clock-segments"
          value={segments}
          onChange={(e) => setSegments(Number(e.target.value))}
        >
          {SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className={common.btnSmall} data-testid="clock-add" disabled={!name.trim()}>
        Add
      </button>
    </form>
  );
}

export default function ClocksPanel({ clocks, canDirect, people = [], channels = [], entries = [] }) {
  // The clock whose audience is being chosen, by id: the dialog is one, for whichever asked.
  const [choosing, setChoosing] = useState(null);
  const chosen = clocks.clocks.find((c) => c.id === choosing) ?? null;
  const kept = entries.filter((e) => !e.visible);
  return (
    <section className={styles.section} aria-label="Clocks" data-testid="clocks-panel">
      <h3 className={styles.caption}>Clocks</h3>
      {clocks.clocks.length === 0 && <p className={styles.empty}>No clock is running.</p>}
      <ul className={styles.list}>
        {clocks.clocks.map((clock) => (
          <ClockItem
            key={clock.id}
            clock={clock}
            clocks={clocks}
            canDirect={canDirect}
            kept={kept}
            onAudience={() => setChoosing(clock.id)}
          />
        ))}
      </ul>
      {canDirect && <AddClock onAdd={clocks.add} />}
      <AudienceDialog
        show={!!chosen}
        title={chosen?.name ?? ''}
        people={people}
        channels={channels}
        nobodyLabel="Nobody - the GM alone"
        onPick={(target) => clocks.edit(chosen.id, { audience: audienceForReveal(target, channels) })}
        onClose={() => setChoosing(null)}
      />
    </section>
  );
}
